import { ReactNode, useCallback, useEffect, useState } from 'react'

import { MeetingsRoomProviderDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'

import { MeetingErrorLayout } from 'modules/meetings/components/Layout/MeetingErrorLayout'
import { RoomContext, type RoomContextState } from 'modules/meetings/lib/context/RoomContext'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useMeetingSoundsSettings } from 'modules/meetings/lib/hooks/useMeetingSounds'

interface RoomProviderProps {
  children: ReactNode
  inPerson?: boolean
}

export const RoomProvider = ({ children, inPerson = false }: RoomProviderProps) => {
  const [isSideBarOpen, setIsSideBarOpen] = useState(true)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [pendingMessagesCount, setPendingMessagesCount] = useState(0)
  const [isTranscriptEnabled, setIsTranscriptEnabled] = useState(true)
  const { enabled: soundsEnabled, setEnabled: setSoundsEnabled } = useMeetingSoundsSettings()
  const { meetingId } = useMeeting()

  const [getOrCreateRoom, { loading: roomLoading, error: roomError, data: roomData }] = useMutation(
    MeetingsRoomProviderDocument,
    {
      extractorFn: (data) => ({
        ...data.meetingsMutations.getOrCreateMeetingsRoom.room,
        errors: data.meetingsMutations.getOrCreateMeetingsRoom.errors || [],
      }),
    }
  )

  const fetchRoom = useCallback(async () => {
    await getOrCreateRoom({
      variables: {
        meetingId: Number(meetingId),
        inPerson: inPerson,
      },
    })
  }, [getOrCreateRoom, meetingId, inPerson])

  useEffect(() => {
    if (meetingId) {
      fetchRoom()
    }
  }, [meetingId, fetchRoom])

  const addPendingMessage = useCallback(() => {
    setPendingMessagesCount((prev) => prev + 1)
  }, [])

  const clearPendingMessages = useCallback(() => {
    setPendingMessagesCount(0)
  }, [])

  const handleChatOpen = useCallback(
    (state: boolean) => {
      setIsChatOpen(state)
      setIsSideBarOpen(false)
      if (state) {
        clearPendingMessages()
      }
    },
    [clearPendingMessages]
  )

  const handleSideBarOpen = useCallback((state: boolean) => {
    setIsSideBarOpen(state)
    setIsChatOpen(false)
  }, [])

  const room = roomData?.meetingsMutations?.getOrCreateMeetingsRoom?.room ?? null

  const value: RoomContextState = {
    isSideBarOpen,
    setIsSideBarOpen: handleSideBarOpen,
    isChatOpen,
    setIsChatOpen: handleChatOpen,
    room,
    loading: roomLoading || !room,
    error: roomError?.message ?? null,
    refetchRoom: fetchRoom,
    soundsEnabled: soundsEnabled ?? true,
    setSoundsEnabled,
    pendingMessagesCount,
    addPendingMessage,
    clearPendingMessages,
    isTranscriptEnabled,
    setIsTranscriptEnabled,
  }

  if (roomError) {
    return <MeetingErrorLayout />
  }

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>
}
